import React from 'react';

import { CheckCircleOutlined as CheckCircleOutlinedIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { parseNullableValue } from '@hrlogics/helper-utils';

import { DataDisputeItemInfoFragment, DataDisputeItemStatus } from 'src/api';
import { Avatar } from 'src/components/shared';
import { DATA_DISPUTE_STATUS_LABEL } from 'src/constants/dataDispute';
import {
  useEmployerBasicInfo,
  useIsControlledByPeoUnderPeoPayrollProvider,
} from 'src/hooks/employer';

export type DataDisputeItemInfoProps = {
  dataDisputeItem: DataDisputeItemInfoFragment;
  onResolve?: (dataDisputeItem: DataDisputeItemInfoFragment) => void;
};

export const DataDisputeItemInfo: React.FC<DataDisputeItemInfoProps> = ({
  dataDisputeItem,
  onResolve,
}) => {
  const isControlledByPeoUnderPeoPayrollProvider = useIsControlledByPeoUnderPeoPayrollProvider();

  const { employer } = useEmployerBasicInfo(dataDisputeItem.employerId);

  const endOfTextIcon = React.useMemo(
    () =>
      dataDisputeItem.status === DataDisputeItemStatus.InProgress ? (
        <CircularProgress size="13px" />
      ) : (
        <CheckCircleOutlinedIcon sx={{ fontSize: '15px' }} />
      ),
    [dataDisputeItem.status],
  );

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
      <Box display="flex" alignItems="center" gap={2}>
        <Avatar width={44} height={44} src={employer?.logo?.downloadUrl} />

        <Box>
          <Typography variant="h6" component="div">
            {parseNullableValue(employer?.displayName)}
          </Typography>

          <Box display="flex" alignItems="center" sx={{ whiteSpace: 'nowrap' }} gap={0.75}>
            <Typography>Status: {DATA_DISPUTE_STATUS_LABEL[dataDisputeItem.status]}</Typography>
            <Box sx={{ fontSize: 0 }}>{endOfTextIcon}</Box>
          </Box>
        </Box>
      </Box>

      {!isControlledByPeoUnderPeoPayrollProvider && (
        <Box display="flex" flexWrap="wrap" gap={1}>
          {onResolve && dataDisputeItem.status === DataDisputeItemStatus.InProgress && (
            <Button variant="outlined" onClick={() => onResolve(dataDisputeItem)}>
              Resolve
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
